'use client';

import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';

import ClearableSearchInput from '@flipster/features/src/input/clearable-search-input';
import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { useRawSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { InstrumentV2ForApiInstanceType } from '@flipster/features/src/shared/snapshot/utils';
import { FormattedMessage } from '@flipster/i18n';
import { Navigator } from '@flipster/react/design-system-legacy/navigator';
import { Badge } from '@flipster/react/design-system/badge';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { Spinner } from '@flipster/react/design-system/spinner';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import DocumentSearchIcon from '@flipster/react/icon/generated/DocumentSearchIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive/index';
import { css } from '@flipster/styled-system/css';
import { flex, vstack } from '@flipster/styled-system/patterns';
import { instrumentFuzzySearch } from '@flipster/utils/fuzzy';
import { instrumentsSort } from '@flipster/utils/sort';

const MobileItemCount = 8;
const DesktopItemCount = 12;

const Page = () => {
  const t = useTranslations();
  const router = useRouter();

  const [searchValue, setSearchValue] = useState<string>('');
  const [page, setPage] = useState<number>(0);

  const { isDesktop } = useBreakpoint();
  const { pswapInstruments, instrumentsKeyBySymbol } = useRawSnapshotSpec();

  const items = useMemo((): InstrumentV2ForApiInstanceType[] => {
    if (!pswapInstruments || !instrumentsKeyBySymbol) return [];

    if (!searchValue.trim()) return pswapInstruments.sort((a, b) => instrumentsSort(a.symbol, b.symbol));
    return instrumentFuzzySearch(
      searchValue,
      pswapInstruments.map((i) => i.symbol),
      instrumentsKeyBySymbol,
    ).map((s) => instrumentsKeyBySymbol[s]);
  }, [pswapInstruments, instrumentsKeyBySymbol, searchValue]);

  const per = isDesktop ? DesktopItemCount : MobileItemCount;
  const firstIndex = page * per;
  const isEmpty = !pswapInstruments || !items.length;

  return (
    <div>
      <div
        className={flex({
          direction: { base: 'column', tablet: 'row' },
          alignItems: { base: 'normal', tablet: 'center' },
          pb: { base: 0, tablet: 20 },
          mb: { base: 0, tablet: 32 },
          borderBottomWidth: { base: 'none', tablet: 1 },
          borderBottomStyle: 'solid',
          borderBottomColor: 'border.default.level-1',
          gap: { base: 24, tablet: 128 },
        })}
      >
        <h1 className={css({ flex: 1, textStyle: 'Title/L', color: 'content.default.level-5' })}>
          <FormattedMessage id='Trade.r251212.Trading_rules' />
        </h1>
        <ClearableSearchInput
          value={searchValue}
          onChange={(value) => {
            setSearchValue(value);
            setPage(0);
          }}
          onClear={() => {
            setPage(0);
          }}
          placeholder={t('Autogen_common.r251126.s_Search_symbol')}
          css={css.raw({ w: { base: '100%', tablet: 400 } })}
        />
      </div>
      <ul
        className={css({
          display: isEmpty ? 'flex' : 'grid',
          justifyContent: isEmpty ? 'center' : 'normal',
          minH: 384,
          marginY: { base: 32, tablet: 24 },
          gridRowGap: { base: 16, tablet: 32 },
          gridColumnGap: { base: 16, tablet: 128 },
          gridTemplateColumns: { base: '1fr', tablet: 'repeat(2, 1fr)' },
          gridTemplateRows: { base: 'repeat(8, 1fr)', tablet: 'repeat(6, 1fr)' },
        })}
      >
        {isEmpty && (
          <li className={flex({ justify: 'center', align: 'center' })}>
            {!pswapInstruments && <Spinner />}
            {pswapInstruments && (
              <div className={vstack({ gap: 4, color: 'content.default.level-2' })}>
                <DocumentSearchIcon />
                <p>
                  <FormattedMessage id='Autogen_common.r251126.s_No_available_symbol' />
                </p>
              </div>
            )}
          </li>
        )}
        {!isEmpty &&
          items.slice(firstIndex, firstIndex + per).map((item) => (
            <li key={item.symbol}>
              <div
                className={flex({
                  justify: 'space-between',
                  align: 'center',
                  w: '100%',
                  h: 34,
                  cursor: 'pointer',
                })}
                onClick={() => router.push(`/support/trading-rules/contract/${item.symbol}`)}
              >
                <div className={flex({ align: 'center', gap: 8 })}>
                  <SymbolDisplay
                    symbol={item.symbol}
                    icon={<SymbolDisplay.Icon size={32} />}
                    mainLabel={
                      <SymbolDisplay.Label
                        value='shortName'
                        css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-5' }}
                      />
                    }
                  />
                  <Badge color='neutral'>
                    <FormattedMessage
                      id='Autogen_common.r251126.s_Perp_item_quoteCurrency'
                      values={{
                        item_quoteCurrency: item.quoteCurrency,
                      }}
                    />
                  </Badge>
                </div>
                <IconButton
                  hierarchy='quaternary'
                  color='content.default.level-3'
                  icon={<ChevronRightIcon size={16} />}
                />
              </div>
            </li>
          ))}
      </ul>
      <Navigator
        onPrev={() => setPage((prev) => prev - 1)}
        onNext={() => setPage((prev) => prev + 1)}
        isPrevDisabled={isEmpty || page === 0}
        isNextDisabled={isEmpty || (page + 1) * per >= items.length}
      />
    </div>
  );
};

export default Page;
